import firebase from "firebase";
import axios from "axios";
import { ensureCheckoutProfile } from "./checkout";

const URL = "https://escuelapp-api-v2.azurewebsites.net/api/connections";
const HEARTBEAT = 30000;

function randomId() {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, value => value.toString(16).padStart(2, "0")).join("");
}

// Web Locks distinguish a duplicated tab (which inherits sessionStorage) from a reload.
function holdTabLock(id) {
  if (!navigator.locks) return Promise.resolve(null);
  return new Promise((resolve, reject) => {
    navigator.locks.request("escuelapp-connection:" + id, { ifAvailable: true }, lock => {
      if (!lock) { resolve(null); return undefined; }
      return new Promise(release => resolve(release));
    }).catch(reject);
  });
}

export class ConnectionSession {
  constructor(options) {
    this.options = options;
    this.state = { status: "checking", uid: null };
    this.listeners = new Set();
    this.generation = 0;
    this.authRevision = 0;
    this.ownerId = options.randomId();
    this.pending = null;
    this.user = null;
  }

  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  publish(state) {
    this.state = state;
    if (this.expiration) this.options.clearTimeout(this.expiration);
    if (state.status === "allowed") {
      this.expiration = this.options.setTimeout(() => {
        this.publish({ status: "checking", uid: this.user && this.user.uid });
        this.renew();
      }, Math.max(0, state.expiresAt - this.options.now()));
    }
    this.listeners.forEach(listener => listener(this.state));
  }

  snapshot() {
    return this.user && this.sessionId && this.token
      ? { uid: this.user.uid, sessionId: this.sessionId, ownerId: this.ownerId, ownerEpoch: this.epoch, token: this.token } : null;
  }

  async sendRelease(snapshot, keepalive = false) {
    if (!snapshot) return;
    try { await this.options.release(snapshot, keepalive); } catch (error) { /* The server lease expires if delivery fails. */ }
  }

  async leave() {
    const old = this.snapshot();
    this.generation++;
    this.pending = null;
    this.user = null;
    this.ownerId = this.options.randomId();
    this.sessionId = null;
    this.token = null;
    if (this.unlock) this.unlock();
    this.unlock = null;
    this.publish({ status: "checking", uid: null });
    await this.sendRelease(old);
  }

  async connect(user) {
    const revision = ++this.authRevision;
    if (!user || user.isAnonymous) {
      await this.leave();
      if (revision === this.authRevision && !this.user) this.publish({ status: "anonymous", uid: null });
      return this.state;
    }
    if (this.user && this.user.uid !== user.uid) await this.leave();
    if (revision !== this.authRevision) return this.state;
    this.user = user;
    return this.renew();
  }

  async renew() {
    if (!this.user || this.paused) return this.state;
    if (this.state.status === "allowed" && this.state.expiresAt <= this.options.now())
      this.publish({ status: "checking", uid: this.user.uid });
    if (this.pending) return this.pending;
    const user = this.user, generation = this.generation;
    const task = this.acquire(user, generation);
    this.pending = task;
    try { return await task; }
    finally { if (this.pending === task) this.pending = null; }
  }

  async acquire(user, generation) {
    let snapshot;
    try {
      if (!this.sessionId) {
        const key = "escuelapp-connection:" + user.uid;
        let id;
        try { id = this.options.storage.getItem(key); } catch (e) { }
        let unlock = id && /^[a-f0-9-]{32,64}$/.test(id) ? await this.options.holdLock(id) : null;
        // Without Web Locks use a fresh ID, so cloned tabs can never share a slot.
        if (!unlock) { id = this.options.randomId(); unlock = await this.options.holdLock(id); }
        if (generation !== this.generation || (!this.user || this.user.uid !== user.uid)) { if (unlock) unlock(); return this.state; }
        this.sessionId = id;
        let previous = 0;
        try { previous = Number(this.options.storage.getItem(key + ":epoch")) || 0; } catch (e) { }
        this.epoch = Number.isSafeInteger(previous) && previous < Number.MAX_SAFE_INTEGER ? previous + 1 : 1;
        this.unlock = unlock;
        try { this.options.storage.setItem(key, id); this.options.storage.setItem(key + ":epoch", String(this.epoch)); } catch (e) { }
      }
      const token = await user.getIdToken();
      snapshot = { uid: user.uid, sessionId: this.sessionId, ownerId: this.ownerId, ownerEpoch: this.epoch, token };
      if (generation !== this.generation || !this.user || this.user.uid !== user.uid) return this.state;
      this.token = token;
      const started = this.options.now();
      const result = await this.options.acquire(snapshot);
      if (generation !== this.generation || !this.user || this.user.uid !== user.uid || this.paused) {
        await this.sendRelease(snapshot);
        return this.state;
      }
      if (result.allowed && (!Number.isFinite(result.leaseSeconds) || result.leaseSeconds <= 0 || result.leaseSeconds > 180))
        throw new Error("Invalid connection lease");
      if (!result.allowed) this.publish({ status: "full", uid: user.uid, limit: result.limit, active: result.active });
      else this.publish({ status: "allowed", uid: user.uid, limit: result.limit, active: result.active, expiresAt: started + result.leaseSeconds * 1000 });
    } catch (error) {
      if (generation !== this.generation || !this.user || this.user.uid !== user.uid) return this.state;
      const status = error.response && error.response.status;
      if (status === 401 || (error.code && error.code.startsWith("auth/"))) {
        this.publish({ status: "auth", uid: user.uid });
      } else if (status === 409) {
        const result = error.response.data;
        this.publish({ status: "full", uid: user.uid, limit: result.limit, active: result.active });
      } else if (this.state.status !== "allowed" || this.state.expiresAt <= this.options.now()) {
        this.publish({ status: "error", uid: user.uid });
      }
    }
    return this.state;
  }

  async suspend() {
    const old = this.snapshot();
    this.paused = true;
    this.ownerId = this.options.randomId();
    this.epoch++;
    try { if (this.user) this.options.storage.setItem("escuelapp-connection:" + this.user.uid + ":epoch", String(this.epoch)); } catch (e) { }
    this.generation++;
    this.pending = null;
    this.publish({ status: "checking", uid: this.user && this.user.uid });
    await this.sendRelease(old, true);
  }

  resume() {
    this.paused = false;
    if (this.state.status === "allowed" && this.state.expiresAt <= this.options.now())
      this.publish({ status: "checking", uid: this.user && this.user.uid });
    return this.renew();
  }
}

export const connectionSession = new ConnectionSession({
  randomId, holdLock: holdTabLock,
  storage: { getItem: key => sessionStorage.getItem(key), setItem: (key, value) => sessionStorage.setItem(key, value) },
  now: () => window.performance.now(), setTimeout: (fn, ms) => window.setTimeout(fn, ms), clearTimeout: id => window.clearTimeout(id),
  acquire: async session => (await axios.post(URL + "/lease", { sessionId: session.sessionId, ownerId: session.ownerId, ownerEpoch: session.ownerEpoch },
    { headers: { Authorization: "Bearer " + session.token }, timeout: 25000 })).data,
  release: async (session, keepalive) => {
    const body = { sessionId: session.sessionId, ownerId: session.ownerId, ownerEpoch: session.ownerEpoch };
    const headers = { Authorization: "Bearer " + session.token, "Content-Type": "application/json" };
    if (keepalive) return window.fetch(URL + "/release", { method: "POST", headers, body: JSON.stringify(body), keepalive: true });
    return axios.post(URL + "/release", body, { headers, timeout: 5000 });
  },
});

export function startConnectionSession() {
  let authGeneration = 0;
  const stopAuth = firebase.auth().onIdTokenChanged(async user => {
    const version = ++authGeneration;
    if (user && !user.isAnonymous) {
      try { await ensureCheckoutProfile(user); } catch (error) { /* The lease remains fail-closed; subscription management can retry. */ }
    }
    if (version === authGeneration) connectionSession.connect(user);
  });
  const heartbeat = window.setInterval(() => connectionSession.renew(), HEARTBEAT);
  const resume = () => connectionSession.resume();
  const hide = () => connectionSession.suspend();
  const visibility = () => { if (document.visibilityState === "visible") resume(); };
  window.addEventListener("focus", resume);
  window.addEventListener("online", resume);
  window.addEventListener("pageshow", resume);
  window.addEventListener("pagehide", hide);
  document.addEventListener("visibilitychange", visibility);
  return () => {
    authGeneration++; stopAuth(); window.clearInterval(heartbeat);
    window.removeEventListener("focus", resume); window.removeEventListener("online", resume);
    window.removeEventListener("pageshow", resume); window.removeEventListener("pagehide", hide);
    document.removeEventListener("visibilitychange", visibility);
    connectionSession.leave();
  };
}



// WEBPACK FOOTER //
// ./app/connectionSession.js